import { startTransition, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { RenderJobList } from '@/components/app/RenderJobList';
import StartRenderModal from '@/components/modals/StartRenderModal';
import { getCreative } from '@/services/creatives';
import { listCreativeRenders } from '@/services/renders';
import type { Creative, RenderJobSummary } from '@/types/api';
import { Button } from '@/components/primitives/Button';
import { creativeEditorLinkState } from '@/rve/editorBackNav';

export default function CreativeRendersPage() {
  const orgId = useOrgId();
  const { creativeId: creativeIdParam, renderId: renderIdParam } = useParams<{
    creativeId: string;
    renderId?: string;
  }>();
  const creativeId = creativeIdParam ? Number.parseInt(creativeIdParam, 10) : Number.NaN;
  const highlightRenderId =
    renderIdParam != null ? Number.parseInt(renderIdParam, 10) : Number.NaN;

  const [creative, setCreative] = useState<Creative | null>(null);
  const [rows, setRows] = useState<RenderJobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [renderModalOpen, setRenderModalOpen] = useState(false);

  const load = useCallback(() => {
    if (Number.isNaN(creativeId)) return;
    startTransition(() => {
      setLoading(true);
      setError('');
    });
    Promise.all([getCreative(creativeId), listCreativeRenders(creativeId)])
      .then(([c, page]) => {
        setCreative(c);
        setRows(page.items);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load renders'))
      .finally(() => setLoading(false));
  }, [creativeId]);

  useEffect(() => {
    load();
  }, [load]);

  if (Number.isNaN(creativeId)) {
    return (
      <div className="aspectly:p-8">
        <p className="aspectly:text-error aspectly:text-sm">Invalid creative ID.</p>
      </div>
    );
  }

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:space-y-8">
      <div className="aspectly:flex aspectly:items-start aspectly:justify-between aspectly:gap-4 aspectly:flex-wrap">
        <div className="aspectly:space-y-2">
          <Link
            to={orgUiPath(orgId, `/creatives/${creativeId}`)}
            state={creativeEditorLinkState('creatives')}
            className="aspectly:inline-flex aspectly:items-center aspectly:gap-2 aspectly:text-primary aspectly:text-sm aspectly:font-bold aspectly:hover:underline"
          >
            <span className="material-symbols-outlined aspectly:text-lg">arrow_back</span>
            Back to editor
          </Link>
          <h1 className="aspectly:text-3xl aspectly:font-headline aspectly:font-black aspectly:text-on-surface">
            Renders{creative?.name ? ` — ${creative.name}` : ''}
          </h1>
          <p className="aspectly:text-sm aspectly:text-on-surface-variant">
            Export jobs for this creative. Share this page URL to link directly to the render list.
          </p>
        </div>
        <Button variant="primary" onClick={() => setRenderModalOpen(true)} disabled={!creative}>
          <span className="material-symbols-outlined aspectly:text-[18px]">movie</span>
          Export video
        </Button>
      </div>

      {error ? (
        <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="aspectly:flex aspectly:items-center aspectly:justify-center aspectly:py-20">
          <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-4xl aspectly:text-primary">
            progress_activity
          </span>
        </div>
      ) : (
        <RenderJobList
          rows={rows}
          highlightRenderId={Number.isNaN(highlightRenderId) ? null : highlightRenderId}
          renderLinkMode="creative"
          emptyMessage="No renders for this creative yet."
        />
      )}

      {creative?.id != null && creative.name ? (
        <StartRenderModal
          isOpen={renderModalOpen}
          onClose={() => setRenderModalOpen(false)}
          creativeId={creative.id}
          creativeName={creative.name}
          onStarted={() => load()}
        />
      ) : null}
    </div>
  );
}
